import { Card } from '@square/web-payments-sdk-types';
import { STORE_CODES } from 'constants/enums';
import SavedCardsDropdown from 'typescript/apps/checkout_v3/components/payment/saved_cards/saved_cards_dropdown';
import SelectedSavedCard from 'typescript/apps/checkout_v3/components/payment/saved_cards/selected_saved_card';
import { useSavedCards } from 'typescript/apps/checkout_v3/hooks/use_saved_cards';
import { useLocaleInfo } from 'typescript/context/locale_info';
import { useSavedCardsContext } from 'typescript/context/saved_cards';
import { WalletPaymentSource } from 'typescript/types/api/payment_sources';
import { onCheckoutPageClick } from 'typescript/utilities/tracking';
import { CHECKOUT_PAGE_CLICK_EVENTS } from 'typescript/constants/tracking';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';

interface SavedCardsContainerProps {
  dropdownTopPosition?: number;
  dropdownLeftPosition?: number;
  setShowCardForm: (show: boolean) => void;
  sqCard: Card;
  canUseSavedCard: boolean;
}

const allowPostalCode = (
  selectedCard: WalletPaymentSource | null,
  storeCode: string,
): boolean => {
  if (selectedCard === null) {
    return false;
  }

  if (!selectedCard?.payment_source?.postal_code?.trim()) {
    return false;
  }

  return storeCode === STORE_CODES.US;
};

const SavedCardsContainer = ({
  dropdownTopPosition = 0,
  dropdownLeftPosition = 0,
  setShowCardForm,
  sqCard,
  canUseSavedCard,
}: SavedCardsContainerProps) => {
  if (!canUseSavedCard) {
    return null;
  }

  const { trackingData } = useCheckoutFlow();
  const { countryCode } = useLocaleInfo();
  const { selectedCard } = useSavedCardsContext();
  const {
    showSavedCards,
    savedCards,
    savedCardsDropdownRef,
    deletionInProgress,
    handlers,
  } = useSavedCards({
    setShowCardForm,
    sqCard,
    canUseSavedCard,
  });

  return (
    <>
      <SelectedSavedCard
        selectedCard={selectedCard}
        usePostalCode={allowPostalCode(selectedCard, countryCode)}
        onCardClicked={(event) => {
          onCheckoutPageClick({
            eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.click_selected_saved_card,
            trackingData,
          });
          handlers.onCardClicked(event);
        }}
      />
      <div ref={savedCardsDropdownRef}>
        <SavedCardsDropdown
          topPosition={dropdownTopPosition}
          leftPosition={dropdownLeftPosition}
          open={showSavedCards}
          deletionInProgress={deletionInProgress}
          savedCards={savedCards}
          onCardSelected={(event) => {
            onCheckoutPageClick({
              eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.select_saved_card,
              trackingData,
            });
            handlers.onCardSelected(event);
          }}
          onCardDeleted={(event) => {
            onCheckoutPageClick({
              eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.delete_saved_card,
              trackingData,
            });
            handlers.onCardDeleted(event);
          }}
        />
      </div>
    </>
  );
};

export default SavedCardsContainer;
